import { FormattedNumber } from 'react-intl'

import { ArrowDownIcon } from '@gto/icons-react/outline/arrow-down'
import { ArrowUpIcon } from '@gto/icons-react/outline/arrow-up'
import { cn } from '@gto/ui/cn'
import { Typography } from '@gto/ui/typography'

type DeltaIndicatorProps = {
  /**
   * Percent change as a whole-number percent (12 == +12%, -3.2 == -3.2%),
   * matching the backend's `delta_pct` contract (current - previous) / previous
   * * 100. May arrive as a Decimal-string; coerce with Number() at the call
   * site. `null`/`undefined` renders a neutral em-dash — the backend returns
   * null when there is no prior window to compare against.
   */
  value: number | null | undefined
  className?: string
}

export const DeltaIndicator = ({ value, className }: DeltaIndicatorProps) => {
  if (value == null) {
    return (
      <Typography
        className={cn('text-sm leading-100', className)}
        color="text.secondary"
        data-tst="delta-neutral"
        variant="caption"
      >
        —
      </Typography>
    )
  }

  const isUp = value >= 0
  const Arrow = isUp ? ArrowUpIcon : ArrowDownIcon

  return (
    <span
      data-tst={isUp ? 'delta-up' : 'delta-down'}
      className={cn(
        'inline-flex items-center gap-1',
        isUp ? 'text-success-main' : 'text-error-main',
        className,
      )}
    >
      <Arrow
        height={12}
        width={12}
      />
      <Typography
        className="text-sm font-medium leading-100"
        color="inherit"
        variant="caption"
      >
        {/* Backend sends whole-number percent already — format directly, do NOT
            use style="percent" (that would multiply by 100 → "1200%"). */}
        <FormattedNumber
          maximumFractionDigits={1}
          minimumFractionDigits={1}
          signDisplay="exceptZero"
          value={value}
        />
        %
      </Typography>
    </span>
  )
}
